import { createContext, useContext, type ReactNode } from 'react';
import type { AxiosInstance } from 'axios';

const HttpClientContext = createContext<AxiosInstance | null>(null);

export interface HttpApiProviderProps {
  client: AxiosInstance;
  children: ReactNode;
}

/**
 * Provides the configured `AxiosInstance` to descendant hooks.
 *
 * Replaces the legacy `ApiClientProvider` that handed out a
 * `MockApiClient` during prototyping. With the real server in place,
 * every hook reads a single axios instance constructed at app boot
 * (`createAxios` in `apps/mobile/lib/api/createAxios.ts`).
 *
 * Tests inject their own instance pointed at MSW's mock server via
 * the same provider so the public surface never knows it's not
 * talking to a real backend.
 */
export function HttpApiProvider({ client, children }: HttpApiProviderProps) {
  return <HttpClientContext.Provider value={client}>{children}</HttpClientContext.Provider>;
}

/**
 * Returns the active axios instance. Throws when used outside a
 * provider so misconfigurations fail loudly during dev rather than
 * silently fall back to a default base URL.
 */
export function useHttpClient(): AxiosInstance {
  const client = useContext(HttpClientContext);
  if (!client) {
    throw new Error(
      'useHttpClient() called without a <HttpApiProvider>. Wrap your app root with <HttpApiProvider client={createAxios(...)}>.',
    );
  }
  return client;
}
